import type {
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';

import type { FilterGroupItem, FilterItem } from '../../utils/filterSettings';
import { DEFAULT_PAGE_SIZE } from '../../utils/tableSettings';
import { DEFAULT_DENSITY, type Density } from '../TableMenu';

/** Every layout dimension "Reset to default" restores. */
export type TableViewState = {
  columnOrder: ColumnOrderState;
  columnVisibility: VisibilityState;
  columnPinning: ColumnPinningState;
  columnSizing: ColumnSizingState;
  pageSize: number;
  density: Density;
};

/**
 * Build the view state a table instance resets to.
 * `defaultColumnVisibility` carries the columns whose meta marks them hidden by default.
 */
export function getDefaultViewState(args: {
  leafColumnIds: string[];
  enableRowSelection: boolean;
  defaultColumnVisibility?: VisibilityState;
  defaultPageSize?: number;
  defaultDensity?: Density;
}): TableViewState {
  const { leafColumnIds, enableRowSelection, defaultColumnVisibility, defaultPageSize, defaultDensity } = args;

  return {
    columnOrder: enableRowSelection ? ['select', ...leafColumnIds] : leafColumnIds,
    columnVisibility: defaultColumnVisibility ?? {},
    columnPinning: {
      left: enableRowSelection ? ['select'] : [],
      right: [],
    },
    columnSizing: {},
    pageSize: defaultPageSize ?? DEFAULT_PAGE_SIZE,
    density: defaultDensity ?? DEFAULT_DENSITY,
  };
}

/** The view dimensions in `current` that diverge from `defaults`, with their current values. */
export function viewChangesFromDefault(current: TableViewState, defaults: TableViewState): Partial<TableViewState> {
  const changes: Partial<TableViewState> = {};

  if (
    current.columnOrder.length !== defaults.columnOrder.length ||
    current.columnOrder.some((id, index) => id !== defaults.columnOrder[index])
  ) {
    changes.columnOrder = current.columnOrder;
  }

  const allVisIds = new Set([...Object.keys(current.columnVisibility), ...Object.keys(defaults.columnVisibility)]);
  if ([...allVisIds].some((id) => (current.columnVisibility[id] ?? true) !== (defaults.columnVisibility[id] ?? true))) {
    changes.columnVisibility = current.columnVisibility;
  }

  if (
    JSON.stringify(current.columnPinning.left) !== JSON.stringify(defaults.columnPinning.left) ||
    JSON.stringify(current.columnPinning.right) !== JSON.stringify(defaults.columnPinning.right)
  ) {
    changes.columnPinning = current.columnPinning;
  }

  if (Object.keys(current.columnSizing).length > 0) changes.columnSizing = current.columnSizing;
  if (current.pageSize !== defaults.pageSize) changes.pageSize = current.pageSize;
  if (current.density !== defaults.density) changes.density = current.density;

  return changes;
}

/** True when any view dimension in `current` diverges from `defaults`. */
export function viewDiffersFromDefault(current: TableViewState, defaults: TableViewState): boolean {
  return Object.keys(viewChangesFromDefault(current, defaults)).length > 0;
}

// Filter item ids are generated per instance, so structural equality drops them and keeps only what the query sends.
// An empty group sends nothing regardless of its operator, so it normalizes to the omitted-filter shape.
const normalizeFilterItem = (item: FilterItem): unknown => {
  if (item.type === 'group') {
    if (item.items.length === 0) return { type: 'group', items: [] };
    return { type: 'group', operator: item.operator, items: item.items.map(normalizeFilterItem) };
  }
  return {
    type: 'line',
    operator: item.operator,
    filterType: item.filterType?.id,
    operatorType: item.operatorType?.id,
    value: item.value,
  };
};

/** True when two filter groups would produce different queries, ignoring generated item ids. */
export function filtersDiffer(a: FilterGroupItem | undefined, b: FilterGroupItem | undefined): boolean {
  const norm = (g: FilterGroupItem | undefined) =>
    JSON.stringify(g ? normalizeFilterItem(g) : { type: 'group', items: [] });
  return norm(a) !== norm(b);
}

/** True when filters, search, or sort differ from the instance defaults (`defaultFilters`, empty search, `defaultSorting`). */
export function queryDiffersFromDefault(
  current: { filters: FilterGroupItem; globalFilter: string; sorting: SortingState },
  defaults: { filters?: FilterGroupItem; sorting?: SortingState } = {},
): boolean {
  const defaultSorting = defaults.sorting ?? [];
  if (filtersDiffer(current.filters, defaults.filters) || current.globalFilter.trim().length > 0) return true;
  if (current.sorting.length !== defaultSorting.length) return true;
  return current.sorting.some((s, i) => s.id !== defaultSorting[i]?.id || s.desc !== defaultSorting[i]?.desc);
}
